import type {
  LocaleCountry,
  LocaleIso,
  LocaleLanguage,
  LocaleMorpheus,
} from "@/enums/locales.enum.ts";

import { localeIsoEnum, localeMorpheusEnum } from "@/enums/locales.enum.ts";

type LocaleDocument = LocaleIso | LocaleLanguage;

type LocaleAny =
  | LocaleDocument
  | LocaleIso
  | LocaleMorpheus
  | Lowercase<LocaleDocument | LocaleIso | LocaleMorpheus>
  | Uppercase<LocaleDocument | LocaleIso | LocaleMorpheus>;

type LocaleFormats = {
  country: LocaleCountry;
  document: LocaleDocument;
  iso: LocaleIso;
  language: LocaleLanguage;
  morpheus: LocaleMorpheus;
};

const localeFormatsMap = Object.freeze([
  {
    morpheus: "nl-NL",
    iso: "nl-NL",
    language: "nl",
    country: "NL",
    document: "nl",
  },
  {
    morpheus: "be-NL",
    iso: "nl-BE",
    language: "nl",
    country: "BE",
    document: "nl-BE",
  },
  {
    iso: "fr-BE",
    document: "fr-BE",
    language: "fr",
    country: "BE",
    morpheus: "be-FR",
  },
  {
    morpheus: "fr-FR",
    iso: "fr-FR",
    language: "fr",
    country: "FR",
    document: "fr",
  },
  {
    morpheus: "dk-DA",
    iso: "da-DK",
    language: "da",
    country: "DK",
    document: "da",
  },
  {
    morpheus: "se-SV",
    iso: "sv-SE",
    language: "sv",
    country: "SE",
    document: "sv",
  },
] as const satisfies LocaleFormats[]);
type LocaleMap = (typeof localeFormatsMap)[number];

function localeToMap(locale: LocaleAny): LocaleMap {
  const lowerCaseLocale = locale.toLowerCase();
  return localeFormatsMap.find(({ iso, document, morpheus }) => {
    return (
      iso.toLowerCase() === lowerCaseLocale ||
      document.toLowerCase() === lowerCaseLocale ||
      morpheus.toLowerCase() === lowerCaseLocale
    );
  })!;
}

const localeToIso = (locale: LocaleAny) => localeToMap(locale).iso;
const localeToMorpheus = (locale: LocaleAny) => localeToMap(locale).morpheus;
const localeToCountry = (locale: LocaleAny) => localeToMap(locale).country;
const localeToLanguage = (locale: LocaleAny) => localeToMap(locale).language;
const localeToDocument = (locale: LocaleAny) => localeToMap(locale).document;

function countryToMaps(country: LocaleCountry): LocaleMap[] {
  return localeFormatsMap.filter((item) => item.country === country);
}

function languageToMaps(language: LocaleLanguage): LocaleMap[] {
  return localeFormatsMap.filter((item) => item.language === language);
}

function isLocaleMorpheus(locale: string): locale is LocaleMorpheus {
  return locale in localeMorpheusEnum;
}
function isLocaleIso(locale: string): locale is LocaleIso {
  return locale in localeIsoEnum;
}

export {
  countryToMaps,
  isLocaleIso,
  isLocaleMorpheus,
  languageToMaps,
  localeToCountry,
  localeToDocument,
  localeToIso,
  localeToLanguage,
  localeToMap,
  localeToMorpheus,
};

export type { LocaleMap };
